import { ChangeEvent, forwardRef, useEffect, useRef } from 'react';
import { IMaskInput } from 'react-imask';
import { EmailPatternMaskProps } from '../FormEmailField.types';
import { InputMask } from 'imask/esm/index';

export const EmailPatternMask = forwardRef<HTMLInputElement, EmailPatternMaskProps>(({ onChange, isMasked, ...props }, ref) => {
    /**
     * RegEx for the dynamic mask for all cases except when we initially mask the value.
     */
    const partialEmailMask: RegExp = /^(?:.*)$/;
    /**
     * RegEx for the dynamic mask only for when isMasked is true. This should only trigger once and then
     * never occur again on only the initial input element value and no subsequent changes.
     */
    const hiddenEmailMask: RegExp = /^(.{2}\*{6}.{2})(@{1})(.+\.{1}.+)$/;
    const matchEmail: RegExp = /(.{1,})(@{1})(.{1,}\.{1}.{1,})/;
    const isMaskedRef = useRef<boolean>(true);

    /**
     * This handles the callback to our input element onChange.
     * We have to construct a psuedo change event manually to pass it to the
     * input element onChange.
     */
    const handleAccept = (value: string, masked: InputMask) => {
        if (onChange && !hiddenEmailMask.test(value)) {
            const event = {
                ...masked.el,
                target: {
                    ...masked.el,
                    value: value,
                    selectionEnd: masked._selection.end,
                },
            } as any as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

            onChange(event);
        }
    };

    useEffect(() => {
        /**
         * This is necessary or the preprare function will permanantly use a stale
         * isMasked state rather than whatever it currently is.
         */
        isMaskedRef.current = isMasked;
    }, [isMasked]);

    return (
        <IMaskInput
            {...props}
            mask={[
                {
                    mask: partialEmailMask,
                },
                {
                    mask: hiddenEmailMask,
                },
            ]}
            /**
             * This prepare function handles the masking of the email regardless of the email length.
             * This runs before the mask is chosen by the internal dispatch function for react-imask.
             */
            prepare={(value: string) => {
                if (isMaskedRef.current) {
                    const email: RegExpMatchArray | null = value.match(matchEmail);

                    if (email !== null) {
                        const emailUserName: string = email[1];
                        const emailSeparator: string = email[2];
                        const emailDomainName: string = email[3];

                        return `${emailUserName.slice(0, 2)}******${emailUserName.slice(emailUserName.length - 2, emailUserName.length)}${emailSeparator}${emailDomainName}`;
                    }
                    else {
                        return value;
                    }
                }
                else {
                    return value;
                }
            }}
            inputRef={ref}
            onAccept={handleAccept}
        />
    );
});